export const WORKBOOK_IMPORT_REGISTRY_META_KEY = 'tableau_import_registry_v1'
export const NODE_SOURCE_IMPORT_ID_KEY = 'source_import_id'

export type WorkbookImportRegistryEntryV1 = {
  import_id: string
  workbook_name?: string | null
  package_path?: string | null
  cloud_workbook_id?: string | null
  source_kind?: string | null
  attached_at?: string | null
}

export type WorkbookImportRegistryV1 = {
  version: 1
  active_import_id: string | null
  entries: WorkbookImportRegistryEntryV1[]
}

const deepClone = <T,>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch {
    return value
  }
}

const normalizeText = (value: any): string => String(value ?? '').trim()
const normalizeKey = (value: any): string => normalizeText(value).toLowerCase()
const isSyntheticBlankCloudImportId = (value: any): boolean =>
  /^blank(?:[-_]|$)/.test(normalizeText(value).toLowerCase())
const isTransientLocalImportId = (value: any): boolean =>
  /^(?:local[-_])/.test(normalizeText(value).toLowerCase())

const asObject = (value: any): Record<string, any> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, any>
}

const asArray = (value: any): any[] => (Array.isArray(value) ? value : [])

const pushUniqueNormalized = (out: string[], seen: Set<string>, value: any): void => {
  const text = normalizeText(value)
  const key = normalizeKey(text)
  if (!text || seen.has(key)) return
  seen.add(key)
  out.push(text)
}

const resolveOwnedImportId = (value: any): string => {
  const direct = normalizeText((value as any)?.[NODE_SOURCE_IMPORT_ID_KEY])
  if (direct) return direct
  const source = asObject((value as any)?.source)
  const tableau = asObject(source?.tableau)
  const nested = normalizeText(tableau?.[NODE_SOURCE_IMPORT_ID_KEY] ?? tableau?.import_id)
  if (nested) return nested
  const meta = asObject((value as any)?.meta)
  return normalizeText(meta?.[NODE_SOURCE_IMPORT_ID_KEY])
}

const withOwnedImportId = (value: any, importId: string): any => {
  const sourceImportId = normalizeText(importId)
  if (!value || typeof value !== 'object' || Array.isArray(value) || !sourceImportId) return value
  const next = { ...(value as Record<string, any>) }
  next[NODE_SOURCE_IMPORT_ID_KEY] = sourceImportId
  const source = asObject(next.source)
  const tableau = asObject(source?.tableau)
  if (tableau) {
    next.source = {
      ...source,
      tableau: {
        ...tableau,
        [NODE_SOURCE_IMPORT_ID_KEY]: sourceImportId,
      },
    }
  }
  return next
}

const ensureOwnedImportId = (value: any, importId: string): any => {
  if (resolveOwnedImportId(value)) return value
  return withOwnedImportId(value, importId)
}

const maybeAnnotateArray = (itemsRaw: any, importId: string): any[] =>
  asArray(itemsRaw).map((entry) => ensureOwnedImportId(entry, importId))

const identityFromKeys = (value: any, keys: string[]): string => {
  const obj = asObject(value)
  if (!obj) return ''
  for (const key of keys) {
    const text = normalizeText(obj[key])
    if (text) return text
  }
  return ''
}

const fieldDictionaryColumnIdentity = (entry: any): string => {
  const obj = asObject(entry)
  if (!obj) return ''
  const datasource = normalizeKey(obj.datasource ?? obj.datasource_name ?? obj.datasource_key)
  const name = normalizeKey(obj.name ?? obj.caption ?? obj.field ?? obj.column)
  return `${datasource}::${name}`
}

const fieldDictionaryInstanceIdentity = (entry: any): string => {
  const obj = asObject(entry)
  if (!obj) return ''
  return normalizeKey(obj.name ?? obj.caption ?? obj.column ?? obj.field)
}

const ensureMetaObject = (workbookIr: any): Record<string, any> => {
  const obj = asObject(workbookIr)
  if (!obj) return {}
  const meta = asObject(obj.meta)
  if (meta) return meta
  obj.meta = {}
  return obj.meta as Record<string, any>
}

export const readWorkbookImportRegistry = (workbookIr: any): WorkbookImportRegistryV1 => {
  const meta = asObject(workbookIr?.meta)
  const raw = meta?.[WORKBOOK_IMPORT_REGISTRY_META_KEY]
  const entries = asArray((raw as any)?.entries)
    .map((entry) => {
      const importId = normalizeText((entry as any)?.import_id)
      if (!importId) return null
      return {
        import_id: importId,
        workbook_name: normalizeText((entry as any)?.workbook_name) || null,
        package_path: normalizeText((entry as any)?.package_path) || null,
        cloud_workbook_id: normalizeText((entry as any)?.cloud_workbook_id) || null,
        source_kind: normalizeText((entry as any)?.source_kind) || null,
        attached_at: normalizeText((entry as any)?.attached_at) || null,
      } satisfies WorkbookImportRegistryEntryV1
    })
    .filter(Boolean) as WorkbookImportRegistryEntryV1[]
  return {
    version: 1,
    active_import_id: normalizeText((raw as any)?.active_import_id) || null,
    entries,
  }
}

export const collectWorkbookOwnedImportIds = (workbookIr: any): string[] => {
  const out: string[] = []
  const seen = new Set<string>()
  const registry = readWorkbookImportRegistry(workbookIr)
  const meta = asObject((workbookIr as any)?.meta)
  pushUniqueNormalized(out, seen, registry.active_import_id)
  pushUniqueNormalized(out, seen, meta?.backend_import_id)
  pushUniqueNormalized(out, seen, meta?.primary_backend_import_id)
  for (const entry of registry.entries) pushUniqueNormalized(out, seen, entry.import_id)
  const addOwnedFromArray = (itemsRaw: any) => {
    for (const entry of asArray(itemsRaw)) {
      pushUniqueNormalized(out, seen, resolveOwnedImportId(entry))
    }
  }
  addOwnedFromArray((workbookIr as any)?.worksheets)
  addOwnedFromArray((workbookIr as any)?.dashboards)
  addOwnedFromArray((workbookIr as any)?.dashboard_layouts)
  addOwnedFromArray((workbookIr as any)?.datasources)
  addOwnedFromArray((workbookIr as any)?.worksheet_specs)
  addOwnedFromArray((workbookIr as any)?.parameters)
  const fd = asObject((workbookIr as any)?.field_dictionary)
  addOwnedFromArray(fd?.columns)
  addOwnedFromArray(fd?.column_instances)
  return out
}

const resolveCurrentWorkbookImportId = (
  workbookIr: any,
  requestedImportIdRaw?: string | null,
): string => {
  const requestedImportId = normalizeText(requestedImportIdRaw)
  const requestedKey = normalizeKey(requestedImportId)
  const registry = readWorkbookImportRegistry(workbookIr)
  const meta = asObject((workbookIr as any)?.meta)
  const activeImportId = normalizeText(registry.active_import_id)
  const backendImportId = normalizeText(meta?.backend_import_id)
  const primaryBackendImportId = normalizeText(meta?.primary_backend_import_id)
  const ownedImportIds = collectWorkbookOwnedImportIds(workbookIr)
  const ownedImportKeys = new Set(ownedImportIds.map((entry) => normalizeKey(entry)))
  if (requestedImportId && (
    ownedImportKeys.has(requestedKey) ||
    requestedKey === normalizeKey(activeImportId) ||
    requestedKey === normalizeKey(backendImportId) ||
    requestedKey === normalizeKey(primaryBackendImportId)
  )) {
    return requestedImportId
  }
  const preferredOwnedImportId = ownedImportIds.find((entry) => !isSyntheticBlankCloudImportId(entry))
  if (preferredOwnedImportId) return preferredOwnedImportId
  if (activeImportId) return activeImportId
  if (backendImportId) return backendImportId
  if (primaryBackendImportId) return primaryBackendImportId
  return requestedImportId
}

const writeWorkbookImportRegistry = (
  workbookIr: any,
  registry: WorkbookImportRegistryV1,
): any => {
  const next = (workbookIr && typeof workbookIr === 'object' && !Array.isArray(workbookIr))
    ? deepClone(workbookIr)
    : {}
  const meta = ensureMetaObject(next)
  meta[WORKBOOK_IMPORT_REGISTRY_META_KEY] = {
    version: 1,
    active_import_id: normalizeText(registry.active_import_id) || null,
    entries: registry.entries.map((entry) => ({
      import_id: normalizeText(entry.import_id),
      workbook_name: normalizeText(entry.workbook_name) || null,
      package_path: normalizeText(entry.package_path) || null,
      cloud_workbook_id: normalizeText(entry.cloud_workbook_id) || null,
      source_kind: normalizeText(entry.source_kind) || null,
      attached_at: normalizeText(entry.attached_at) || null,
    })),
  }
  return next
}

export const ensureWorkbookIrImportOwnership = (
  workbookIr: any,
  fallbackImportIdRaw: string | null | undefined,
  meta?: Omit<WorkbookImportRegistryEntryV1, 'import_id'>,
): any => {
  const fallbackImportId = normalizeText(fallbackImportIdRaw)
  if (!workbookIr || typeof workbookIr !== 'object' || Array.isArray(workbookIr)) return workbookIr
  const existingRegistry = readWorkbookImportRegistry(workbookIr)
  const defaultImportId =
    fallbackImportId ||
    normalizeText(existingRegistry.active_import_id) ||
    normalizeText((workbookIr as any)?.meta?.backend_import_id) ||
    normalizeText((workbookIr as any)?.meta?.primary_backend_import_id)
  if (!defaultImportId) return deepClone(workbookIr)
  const next = deepClone(workbookIr)
  const metaObj = ensureMetaObject(next)
  const existingMetaImportId = normalizeText(metaObj.backend_import_id)
  const existingPrimaryImportId = normalizeText(metaObj.primary_backend_import_id)
  const shouldReplaceSyntheticBlankMetaImportId =
    Boolean(fallbackImportId) &&
    !isSyntheticBlankCloudImportId(fallbackImportId) &&
    isSyntheticBlankCloudImportId(existingMetaImportId)
  const shouldReplaceSyntheticBlankPrimaryImportId =
    Boolean(fallbackImportId) &&
    !isSyntheticBlankCloudImportId(fallbackImportId) &&
    isSyntheticBlankCloudImportId(existingPrimaryImportId)
  if (!existingMetaImportId || shouldReplaceSyntheticBlankMetaImportId) {
    metaObj.backend_import_id = defaultImportId
  }
  if (!existingPrimaryImportId || shouldReplaceSyntheticBlankPrimaryImportId) {
    metaObj.primary_backend_import_id =
      normalizeText(metaObj.backend_import_id) || defaultImportId
  }
  next.worksheets = maybeAnnotateArray(next.worksheets, defaultImportId)
  next.dashboards = maybeAnnotateArray(next.dashboards, defaultImportId)
  next.dashboard_layouts = maybeAnnotateArray(next.dashboard_layouts, defaultImportId)
  next.datasources = maybeAnnotateArray(next.datasources, defaultImportId)
  next.worksheet_specs = maybeAnnotateArray(next.worksheet_specs, defaultImportId)
  next.parameters = maybeAnnotateArray(next.parameters, defaultImportId)
  const fd = asObject(next.field_dictionary)
  if (fd) {
    fd.columns = maybeAnnotateArray(fd.columns, defaultImportId)
    fd.column_instances = maybeAnnotateArray(fd.column_instances, defaultImportId)
  }
  const nextRegistry = readWorkbookImportRegistry(next)
  const byId = new Map<string, WorkbookImportRegistryEntryV1>()
  for (const entry of nextRegistry.entries) byId.set(entry.import_id, entry)
  const current = byId.get(defaultImportId)
  byId.set(defaultImportId, {
    import_id: defaultImportId,
    workbook_name: normalizeText(meta?.workbook_name ?? current?.workbook_name ?? (next as any)?.meta?.name) || null,
    package_path: normalizeText(meta?.package_path ?? current?.package_path) || null,
    cloud_workbook_id: normalizeText(meta?.cloud_workbook_id ?? current?.cloud_workbook_id ?? (next as any)?.meta?.cloud_workbook_id) || null,
    source_kind: normalizeText(meta?.source_kind ?? current?.source_kind) || null,
    attached_at: normalizeText(meta?.attached_at ?? current?.attached_at) || null,
  })
  return writeWorkbookImportRegistry(next, {
    version: 1,
    active_import_id: defaultImportId,
    entries: Array.from(byId.values()),
  })
}

const collectionIdentitySet = (itemsRaw: any, identityKeys: string[], ownerId?: string | null): Set<string> => {
  const out = new Set<string>()
  const ownerKey = normalizeKey(ownerId)
  for (const entry of asArray(itemsRaw)) {
    if (ownerKey && normalizeKey(resolveOwnedImportId(entry)) !== ownerKey) continue
    const identity = normalizeKey(identityFromKeys(entry, identityKeys))
    if (identity) out.add(identity)
  }
  return out
}

const setsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false
  for (const entry of a) {
    if (!b.has(entry)) return false
  }
  return true
}

const remapOwnedImportIdInEntry = (value: any, fromImportId: string, toImportId: string): any => {
  const fromKey = normalizeKey(fromImportId)
  const toValue = normalizeText(toImportId)
  if (!value || typeof value !== 'object' || Array.isArray(value) || !fromKey || !toValue) return value
  if (normalizeKey(resolveOwnedImportId(value)) !== fromKey) return value
  return withOwnedImportId(value, toValue)
}

export const remapOwnedImportIdAcrossWorkbook = (workbookIr: any, fromImportId: string, toImportId: string): any => {
  const fromKey = normalizeText(fromImportId)
  const toKey = normalizeText(toImportId)
  if (!workbookIr || typeof workbookIr !== 'object' || Array.isArray(workbookIr) || !fromKey || !toKey || fromKey === toKey) {
    return deepClone(workbookIr)
  }
  const next = deepClone(workbookIr)
  const remapArray = (itemsRaw: any): any[] => asArray(itemsRaw).map((entry) => remapOwnedImportIdInEntry(entry, fromKey, toKey))
  next.worksheets = remapArray(next.worksheets)
  next.dashboards = remapArray(next.dashboards)
  next.dashboard_layouts = remapArray(next.dashboard_layouts)
  next.datasources = remapArray(next.datasources)
  next.worksheet_specs = remapArray(next.worksheet_specs)
  next.parameters = remapArray(next.parameters)
  const fd = asObject(next.field_dictionary)
  if (fd) {
    fd.columns = remapArray(fd.columns)
    fd.column_instances = remapArray(fd.column_instances)
  }
  const meta = ensureMetaObject(next)
  if (normalizeKey(meta.backend_import_id) === normalizeKey(fromKey)) meta.backend_import_id = toKey
  if (normalizeKey(meta.primary_backend_import_id) === normalizeKey(fromKey)) meta.primary_backend_import_id = toKey
  const registry = readWorkbookImportRegistry(next)
  const entries = registry.entries
    .map((entry) => (
      normalizeKey(entry.import_id) === normalizeKey(fromKey)
        ? { ...entry, import_id: toKey }
        : entry
    ))
    .reduce<WorkbookImportRegistryEntryV1[]>((acc, entry) => {
      const existingIndex = acc.findIndex((candidate) => normalizeKey(candidate.import_id) === normalizeKey(entry.import_id))
      if (existingIndex >= 0) {
        acc[existingIndex] = {
          ...acc[existingIndex],
          ...entry,
          import_id: normalizeText(entry.import_id),
        }
      } else {
        acc.push(entry)
      }
      return acc
    }, [])
  return writeWorkbookImportRegistry(next, {
    version: 1,
    active_import_id: normalizeKey(registry.active_import_id) === normalizeKey(fromKey) ? toKey : registry.active_import_id,
    entries,
  })
}

const matchesImportedWorkbookLineage = (args: {
  currentWorkbookIr: any
  currentImportId: string
  incomingWorkbookIr: any
  incomingImportId: string
}): boolean => {
  const currentImportId = normalizeText(args.currentImportId)
  const incomingImportId = normalizeText(args.incomingImportId)
  if (!currentImportId || !incomingImportId || normalizeKey(currentImportId) === normalizeKey(incomingImportId)) {
    return false
  }
  const current = args.currentWorkbookIr
  const incoming = args.incomingWorkbookIr
  if (!current || typeof current !== 'object' || Array.isArray(current) || !incoming || typeof incoming !== 'object' || Array.isArray(incoming)) {
    return false
  }
  const currentMeta = asObject((current as any).meta)
  const incomingMeta = asObject((incoming as any).meta)
  const currentBackendImportId = normalizeText(currentMeta?.backend_import_id ?? currentMeta?.primary_backend_import_id)
  if (currentBackendImportId && normalizeKey(currentBackendImportId) === normalizeKey(incomingImportId)) {
    return true
  }
  const currentRegistry = readWorkbookImportRegistry(current)
  const incomingRegistry = readWorkbookImportRegistry(incoming)
  const currentEntry = currentRegistry.entries.find((entry) => normalizeKey(entry.import_id) === normalizeKey(currentImportId))
  const incomingEntry = incomingRegistry.entries.find((entry) => normalizeKey(entry.import_id) === normalizeKey(incomingImportId))
  const currentPackagePath = normalizeKey(currentEntry?.package_path)
  const incomingPackagePath = normalizeKey(incomingEntry?.package_path)
  if (currentPackagePath && incomingPackagePath && currentPackagePath === incomingPackagePath) {
    return true
  }
  const currentWorkbookName = normalizeKey(currentEntry?.workbook_name || currentMeta?.name || (current as any)?.name)
  const incomingWorkbookName = normalizeKey(incomingEntry?.workbook_name || incomingMeta?.name || (incoming as any)?.name)
  if (!currentWorkbookName || !incomingWorkbookName || currentWorkbookName !== incomingWorkbookName) {
    return false
  }
  const worksheetKeys = ['name', 'worksheet_name', 'internal_name']
  const dashboardKeys = ['name', 'dashboard_name', 'internal_name']
  const currentWorksheets = collectionIdentitySet(current.worksheets, worksheetKeys, currentImportId)
  const incomingWorksheets = collectionIdentitySet(incoming.worksheets, worksheetKeys)
  const currentDashboards = collectionIdentitySet(current.dashboards, dashboardKeys, currentImportId)
  const incomingDashboards = collectionIdentitySet(incoming.dashboards, dashboardKeys)
  return setsEqual(currentWorksheets, incomingWorksheets) && setsEqual(currentDashboards, incomingDashboards)
}

const collectImportPromotionCandidates = (args: {
  currentWorkbookIr: any
  currentImportId?: string | null
}): string[] => {
  const current = args.currentWorkbookIr
  if (!current || typeof current !== 'object' || Array.isArray(current)) return []
  const candidates: string[] = []
  const seen = new Set<string>()
  const pushCandidate = (value: any) => {
    const candidate = normalizeText(value)
    const candidateKey = normalizeKey(candidate)
    if (!candidate || seen.has(candidateKey)) return
    seen.add(candidateKey)
    candidates.push(candidate)
  }
  pushCandidate(args.currentImportId)
  for (const ownedImportId of collectWorkbookOwnedImportIds(current)) {
    pushCandidate(ownedImportId)
  }
  return candidates
}

const resolveImportPromotionSource = (args: {
  currentWorkbookIr: any
  currentImportId?: string | null
  incomingWorkbookIr: any
  incomingImportId: string
}): string => {
  const current = args.currentWorkbookIr
  const candidates = collectImportPromotionCandidates(args)
  for (const candidate of candidates) {
    if (matchesImportedWorkbookLineage({
      currentWorkbookIr: current,
      currentImportId: candidate,
      incomingWorkbookIr: args.incomingWorkbookIr,
      incomingImportId: args.incomingImportId,
    })) {
      return candidate
    }
  }
  return ''
}

const mergeOwnedNamedCollection = (args: {
  currentItems: any[]
  incomingItems: any[]
  identityKeys: string[]
  collectionLabel: string
  debugContext?: Record<string, any>
}) => {
  const out = [...args.currentItems]
  const indexByIdentity = new Map<string, number>()
  for (let idx = 0; idx < out.length; idx += 1) {
    const identity = normalizeKey(identityFromKeys(out[idx], args.identityKeys))
    if (identity) indexByIdentity.set(identity, idx)
  }
  for (const incomingItem of args.incomingItems) {
    const identity = normalizeKey(identityFromKeys(incomingItem, args.identityKeys))
    if (!identity) {
      out.push(incomingItem)
      continue
    }
    const existingIndex = indexByIdentity.get(identity)
    if (existingIndex == null) {
      indexByIdentity.set(identity, out.length)
      out.push(incomingItem)
      continue
    }
    const existing = out[existingIndex]
    const existingOwner = resolveOwnedImportId(existing)
    const incomingOwner = resolveOwnedImportId(incomingItem)
    if (existingOwner && incomingOwner && existingOwner !== incomingOwner) {
      try {
        console.warn(
          `[FG-IMPORT-MERGE-CONFLICT] ${JSON.stringify({
            ...args.debugContext,
            collection_label: args.collectionLabel,
            identity: identityFromKeys(incomingItem, args.identityKeys),
            existing_owner: existingOwner,
            incoming_owner: incomingOwner,
          })}`,
        )
      } catch {
        console.warn('[FG-IMPORT-MERGE-CONFLICT]', {
          ...args.debugContext,
          collection_label: args.collectionLabel,
          identity: identityFromKeys(incomingItem, args.identityKeys),
          existing_owner: existingOwner,
          incoming_owner: incomingOwner,
        })
      }
      throw new Error(
        `Cannot merge Tableau imports because ${args.collectionLabel} "${identityFromKeys(incomingItem, args.identityKeys)}" exists in multiple imports.`,
      )
    }
    out[existingIndex] = incomingItem
  }
  return out
}

const mergeOwnerScopedNamedCollection = (args: {
  currentItems: any[]
  incomingItems: any[]
  identityKeys: string[]
}): any[] => {
  const out = [...args.currentItems]
  const seen = new Set<string>()
  const buildKey = (entry: any): string => {
    const owner = normalizeKey(resolveOwnedImportId(entry))
    const identity = normalizeKey(identityFromKeys(entry, args.identityKeys))
    return `${owner}::${identity}`
  }
  for (const entry of out) {
    const key = buildKey(entry)
    if (key !== '::') seen.add(key)
  }
  for (const incomingItem of args.incomingItems) {
    const key = buildKey(incomingItem)
    if (key === '::') {
      out.push(incomingItem)
      continue
    }
    if (seen.has(key)) continue
    seen.add(key)
    out.push(incomingItem)
  }
  return out
}

const mergeOwnedFieldDictionaryCollection = (
  currentItems: any[],
  incomingItems: any[],
  identityFn: (entry: any) => string,
): any[] => {
  const out = [...currentItems]
  const seen = new Set<string>()
  for (const entry of out) {
    const owner = normalizeKey(resolveOwnedImportId(entry))
    const identity = normalizeKey(identityFn(entry))
    seen.add(`${owner}::${identity}`)
  }
  for (const entry of incomingItems) {
    const owner = normalizeKey(resolveOwnedImportId(entry))
    const identity = normalizeKey(identityFn(entry))
    const key = `${owner}::${identity}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(entry)
  }
  return out
}

export const mergeTableauWorkbookIrs = (args: {
  currentWorkbookIr: any
  currentImportId?: string | null
  incomingWorkbookIr: any
  incomingImportId: string
  incomingMeta?: Omit<WorkbookImportRegistryEntryV1, 'import_id'>
}): any => {
  const incomingImportId = normalizeText(args.incomingImportId)
  if (!incomingImportId) return deepClone(args.currentWorkbookIr ?? args.incomingWorkbookIr ?? null)
  const incomingAnnotated = ensureWorkbookIrImportOwnership(args.incomingWorkbookIr, incomingImportId, args.incomingMeta)
  const promotionCandidateImportIds = collectImportPromotionCandidates({
    currentWorkbookIr: args.currentWorkbookIr,
    currentImportId: args.currentImportId,
  })
  const promotionSourceImportId = resolveImportPromotionSource({
    currentWorkbookIr: args.currentWorkbookIr,
    currentImportId: args.currentImportId,
    incomingWorkbookIr: incomingAnnotated,
    incomingImportId,
  })
  const incoming = promotionSourceImportId
    ? remapOwnedImportIdAcrossWorkbook(incomingAnnotated, promotionSourceImportId, incomingImportId)
    : incomingAnnotated
  const currentImportId = resolveCurrentWorkbookImportId(
    args.currentWorkbookIr,
    promotionSourceImportId || args.currentImportId,
  )
  const currentAnnotated = ensureWorkbookIrImportOwnership(args.currentWorkbookIr, currentImportId)
  const current = promotionSourceImportId
    ? remapOwnedImportIdAcrossWorkbook(currentAnnotated, promotionSourceImportId, incomingImportId)
    : currentAnnotated
  if (!current || typeof current !== 'object' || Array.isArray(current)) return incoming
  if (!incoming || typeof incoming !== 'object' || Array.isArray(incoming)) return current
  const mergeDebugContext = {
    requested_current_import_id: normalizeText(args.currentImportId) || null,
    resolved_current_import_id: currentImportId || null,
    incoming_import_id: incomingImportId,
    promotion_source_import_id: promotionSourceImportId || null,
    promotion_candidate_import_ids: promotionCandidateImportIds,
    current_owned_import_ids: collectWorkbookOwnedImportIds(current),
    incoming_owned_import_ids: collectWorkbookOwnedImportIds(incoming),
    current_workbook_name: normalizeText((current as any)?.meta?.name ?? (current as any)?.name) || null,
    incoming_workbook_name: normalizeText((incoming as any)?.meta?.name ?? (incoming as any)?.name) || null,
  }
  const next = deepClone(current)
  const nextMeta = ensureMetaObject(next)
  const incomingMeta = ensureMetaObject(incoming)
  next.name = next.name ?? incoming.name ?? null
  nextMeta.name = normalizeText(nextMeta.name || incomingMeta.name || next.name || incoming.name) || null
  nextMeta.primary_backend_import_id = normalizeText(
    nextMeta.primary_backend_import_id || nextMeta.backend_import_id || incomingMeta.primary_backend_import_id || incomingMeta.backend_import_id,
  ) || null
  nextMeta.backend_import_id = incomingImportId
  nextMeta.cloud_workbook_id = normalizeText(nextMeta.cloud_workbook_id || incomingMeta.cloud_workbook_id) || null

  next.datasources = mergeOwnerScopedNamedCollection({
    currentItems: asArray(next.datasources),
    incomingItems: asArray(incoming.datasources),
    identityKeys: ['datasource_id', 'datasource_key', 'name', 'datasource_name'],
  })
  next.worksheets = mergeOwnedNamedCollection({
    currentItems: asArray(next.worksheets),
    incomingItems: asArray(incoming.worksheets),
    identityKeys: ['name', 'worksheet_name', 'internal_name'],
    collectionLabel: 'worksheet',
    debugContext: mergeDebugContext,
  })
  next.dashboards = mergeOwnedNamedCollection({
    currentItems: asArray(next.dashboards),
    incomingItems: asArray(incoming.dashboards),
    identityKeys: ['name', 'dashboard_name', 'internal_name'],
    collectionLabel: 'dashboard',
    debugContext: mergeDebugContext,
  })
  next.dashboard_layouts = mergeOwnedNamedCollection({
    currentItems: asArray(next.dashboard_layouts),
    incomingItems: asArray(incoming.dashboard_layouts),
    identityKeys: ['name'],
    collectionLabel: 'dashboard layout',
    debugContext: mergeDebugContext,
  })
  next.worksheet_specs = mergeOwnedNamedCollection({
    currentItems: asArray(next.worksheet_specs),
    incomingItems: asArray(incoming.worksheet_specs),
    identityKeys: ['worksheet_name', 'internal_name', 'name'],
    collectionLabel: 'worksheet spec',
    debugContext: mergeDebugContext,
  })
  next.parameters = mergeOwnedNamedCollection({
    currentItems: asArray(next.parameters),
    incomingItems: asArray(incoming.parameters),
    identityKeys: ['name', 'caption'],
    collectionLabel: 'parameter',
    debugContext: mergeDebugContext,
  })

  const nextFieldDictionary = asObject(next.field_dictionary) ?? {}
  const incomingFieldDictionary = asObject(incoming.field_dictionary) ?? {}
  nextFieldDictionary.columns = mergeOwnedFieldDictionaryCollection(
    asArray(nextFieldDictionary.columns),
    asArray(incomingFieldDictionary.columns),
    fieldDictionaryColumnIdentity,
  )
  nextFieldDictionary.column_instances = mergeOwnedFieldDictionaryCollection(
    asArray(nextFieldDictionary.column_instances),
    asArray(incomingFieldDictionary.column_instances),
    fieldDictionaryInstanceIdentity,
  )
  if (Object.keys(nextFieldDictionary).length > 0) next.field_dictionary = nextFieldDictionary

  const mergedRegistry = readWorkbookImportRegistry(next)
  const incomingRegistry = readWorkbookImportRegistry(incoming)
  const registryById = new Map<string, WorkbookImportRegistryEntryV1>()
  for (const entry of [...mergedRegistry.entries, ...incomingRegistry.entries]) {
    registryById.set(entry.import_id, {
      ...(registryById.get(entry.import_id) || {}),
      ...entry,
      import_id: entry.import_id,
    })
  }
  nextMeta[WORKBOOK_IMPORT_REGISTRY_META_KEY] = {
    version: 1,
    active_import_id: incomingImportId,
    entries: Array.from(registryById.values()),
  }
  return next
}

export const resolveWorksheetOwnedImportId = (
  workbookIr: any,
  worksheetSpec: any,
): string => {
  const direct = resolveOwnedImportId(worksheetSpec)
  if (direct) return direct
  const worksheetNameCandidates = [
    (worksheetSpec as any)?.source?.tableau?.worksheet_name,
    (worksheetSpec as any)?.source?.tableau?.worksheetName,
    (worksheetSpec as any)?.name,
  ]
    .map(normalizeText)
    .filter(Boolean)
  const matchesName = (value: any): boolean => {
    const candidates = [
      (value as any)?.name,
      (value as any)?.worksheet_name,
      (value as any)?.internal_name,
    ].map(normalizeText).filter(Boolean)
    return worksheetNameCandidates.some((name) => candidates.some((candidate) => normalizeKey(candidate) === normalizeKey(name)))
  }
  for (const entry of asArray((workbookIr as any)?.worksheet_specs)) {
    if (matchesName(entry)) {
      const owned = resolveOwnedImportId(entry)
      if (owned) return owned
    }
  }
  for (const entry of asArray((workbookIr as any)?.worksheets)) {
    if (matchesName(entry)) {
      const owned = resolveOwnedImportId(entry)
      if (owned) return owned
    }
  }
  const registry = readWorkbookImportRegistry(workbookIr)
  return normalizeText(registry.active_import_id)
}
